import { LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';

/**
 * Which actions the "…" menu of a Home day item offers.
 *
 * This lives outside the `cs-day-items` component so it can be unit-tested
 * without Angular, the same way `pages/training/workout-options-policy.ts` does
 * it for the Training page. The component pulls in the whole Angular dependency
 * graph and cannot be loaded by the plain-Node test runner.
 *
 * Why it exists at all (deferred finding `df-580eb1e35ed6`): the decision used
 * to sit inside `getAdditionalActions`, mixed in with the translated labels and
 * the tap handlers, so no test could reach it. That matters most for workouts,
 * where this menu and the Training page's must agree -- they once did not, and
 * the Training page offered "Skip" on a finished workout (`df-f14e97e159a5`,
 * #465). The two are still separate decisions, because this one also covers
 * meals, weigh-ins, weekly checkups and the day start / day end items.
 *
 * The component keeps the parts that need Angular: the translated label for
 * each kind and the handler behind it.
 */

/** One action the Home day item menu can offer. */
export enum DayItemActionKind {
  /** Mark the item as skipped. */
  Skip = 'Skip',
  /** Meal only: type the meal's macros in by hand. */
  LogMacrosManually = 'LogMacrosManually',
  /** Meal only: let the meal planner suggest ingredients. */
  AutoSuggestions = 'AutoSuggestions',
  /** Meal only: fill the meal from a photo. */
  ScanMeal = 'ScanMeal',
  /** Meal only: remove every ingredient from the meal. */
  ClearMeal = 'ClearMeal',
  /** Undo a skip (or, for meals and weigh-ins, a completion). */
  Restore = 'Restore',
  /** Move the item to another time or day. */
  Reschedule = 'Reschedule',
  /** Workout only: open the plan editor on this workout. */
  Edit = 'Edit',
}

/** Everything the decision depends on. */
export interface DayItemActionsState {
  /** What kind of item this is. */
  type: LogRecordType;
  /** The item's stored state. */
  state: LogRecordState;
  /** Meal only: whether the meal already has at least one ingredient. */
  mealHasIngredients: boolean;
  /**
   * Whether the item's own date is before today. This is the item's date, not
   * the date of the day the user is looking at.
   */
  isPastDay: boolean;
}

/**
 * Which actions to offer, in the order they appear in the menu.
 *
 * The "no workout today" placeholder is not handled here: the component gives
 * it no menu before it ever asks this function.
 *
 * The rules, in the order they are applied:
 *
 * 1. An item that is neither skipped nor done offers "Skip". A meal in that
 *    state also offers "Log Macros Manually", "Auto Suggestions", "Scan Meal"
 *    and "Clear Meal" (only when it already has ingredients). A finished workout deliberately gets no "Skip": see #465.
 * 2. "Restore" undoes a skip for every kind of item that can be restored, and
 *    also undoes a completion for meals and weigh-ins. A done workout or weekly
 *    checkup cannot be restored from here.
 * 3. "Reschedule" is offered for an item that is neither skipped nor done, is
 *    not on a past day, and is not a weekly checkup.
 * 4. A workout always offers "Edit".
 * 5. The day start and day end items always offer "Reschedule", even when done
 *    or on a past day -- added last, only when rule 3 did not already add it.
 */
export function getDayItemActionKinds(input: DayItemActionsState): DayItemActionKind[] {
  const kinds: DayItemActionKind[] = [];
  const { state, type } = input;
  const isOpen = state !== LogRecordState.Skipped && state !== LogRecordState.Done;
  const isMeal = type === LogRecordType.Meal;

  // if neither skipped nor done: skip
  if (isOpen) {
    kinds.push(DayItemActionKind.Skip);
  }

  // if an open meal: the meal-only actions, right after skip
  if (isOpen && isMeal) {
    kinds.push(DayItemActionKind.LogMacrosManually);
    kinds.push(DayItemActionKind.AutoSuggestions);
    kinds.push(DayItemActionKind.ScanMeal);
    if (input.mealHasIngredients) {
      kinds.push(DayItemActionKind.ClearMeal);
    }
  }

  // if a meal or weigh-in is done or skipped, or a workout or checkup is skipped: restore
  const restoresDoneOrSkipped = type === LogRecordType.Meal || type === LogRecordType.WeighIn;
  const restoresSkippedOnly = type === LogRecordType.Workout || type === LogRecordType.WeeklyCheckup;
  if ((restoresDoneOrSkipped && !isOpen) || (restoresSkippedOnly && state === LogRecordState.Skipped)) {
    kinds.push(DayItemActionKind.Restore);
  }

  // if still open, not in the past and not a weekly checkup: reschedule
  if (isOpen && !input.isPastDay && type !== LogRecordType.WeeklyCheckup) {
    kinds.push(DayItemActionKind.Reschedule);
  }

  // if a workout: edit
  if (type === LogRecordType.Workout) {
    kinds.push(DayItemActionKind.Edit);
  }

  // if day start or day end: always reschedule, even when done or in the past
  const isDayBoundary = type === LogRecordType.DayEnd || type === LogRecordType.DayStart;
  if (isDayBoundary && !kinds.includes(DayItemActionKind.Reschedule)) {
    kinds.push(DayItemActionKind.Reschedule);
  }

  return kinds;
}
